import { api } from '../../api/client';
import { AskFor } from '../../components/AskStitch';
import { useTenant } from '../../components/Shell';
import { Card, Chip, LoadError, Loading, PageHeader } from '../../components/ui';
import { useData } from '../../lib/useData';
import { FEATURE_STATE } from '../admin/FeaturesCard';

/**
 * What's on for your company (1.13.0, Teampace 3000): every feature, whether it's on, and why not,
 * with who turns it on. Where only Stitch can, the company's admins ask from here (Ask Stitch to turn
 * this on), and see the answer. Read-only otherwise: the company's own settings stay in Settings.
 */
export function TenantWhatsOn() {
  const { tenant } = useTenant();
  const data = useData(() => api.companyFeatures(tenant.domain), [tenant.domain], { live: false });
  const list = data.data?.features ?? [];
  const on = list.filter((f) => f.state === 'on');
  const notOn = list.filter((f) => f.state !== 'on');
  return (
    <>
      <PageHeader eyebrow="Company" title="What’s on" />
      <div className="content">
        <p className="muted page-lede">
          Everything Stitch does for {tenant.name}, whether it’s on, and why not. Your company’s admins change your own settings in Settings; for the rest, they can ask
          Stitch to turn it on here.
        </p>
        {data.loading && !data.data ? (
          <Loading />
        ) : data.error ? (
          <LoadError error={data.error} retry={data.reload} />
        ) : (
          <>
            {notOn.length > 0 && (
              <Card title="Not on" id="not-on" action={<span className="muted">{notOn.length}</span>}>
                <ul className="features" aria-label="Not on">
                  {notOn.map((f) => (
                    <li key={f.id} id={`feature-${f.id}`} className={`feature ${f.state}`}>
                      <div className="feature-main">
                        <div className="feature-title">
                          <strong>{f.name}</strong>
                          <Chip small tone={FEATURE_STATE[f.state].tone}>
                            {f.state === 'blocked' ? 'Not available yet' : 'Off'}
                          </Chip>
                        </div>
                        <span className="muted">{f.what}</span>
                        {f.reason && <span className="feature-reason">{f.reason}</span>}
                        <span className="feature-owner muted">{f.ownerWords}</span>
                      </div>
                      <div className="feature-control">
                        <AskFor tenant={tenant.domain} f={f} admin={data.data!.admin} admins={data.data!.admins} onAsked={data.reload} compact={false} />
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
            <Card title="On" id="on" action={<span className="muted">{on.length}</span>}>
              <ul className="features" aria-label="On">
                {on.map((f) => (
                  <li key={f.id} id={`feature-${f.id}`} className="feature on">
                    <div className="feature-main">
                      <div className="feature-title">
                        <strong>{f.name}</strong>
                        <Chip small tone="ok">
                          On
                        </Chip>
                      </div>
                      <span className="muted">{f.what}</span>
                      {f.detail && <span className="muted">{f.detail}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          </>
        )}
      </div>
    </>
  );
}
